<template>
  <div class="share-bill-link__wrapper">
    <base-popup
      class="share-bill-link"
      ref="popup"
      v-show="!notificationShowing"
      :without-icon="true"
    >
      <template #header="{ close }">
        <div class="share-bill-link__header">
          <div class="share-bill-link__title">
            {{ $t('bills.popup.share-bill-link.title') }}
          </div>
          <popup-close-button
            class="share-bill-link__close"
            @click="close"
          ></popup-close-button>
        </div>
      </template>
      <template #content>
        <div class="share-bill-link__content">
          <text-area-input
            class="share-bill-link__text"
            v-model:value="content"
            :can-copy="true"
            :title="$t('share-bill-link-popup.recipient')"
            @copied="handleLinkCopied"
          ></text-area-input>
          <div class="share-bill-link__input">
            <link-input
              :disabled="true"
              :title="$t('share-bill-link-popup.invoice')"
              v-model:value="bill.link"
              :can-copy="true"
              @copied="handleLinkCopied"
            ></link-input>
            <social-share-buttons
              class="share-bill-link__buttons"
              :enabled-socials="enabledSocials"
              :url-to-share="_bill.link"
              :content-to-share="content"
              @needAction="handleShareAction"
            ></social-share-buttons>
          </div>
        </div>
      </template>
    </base-popup>
    <success-notification
      ref="billLinkCopiedNotification"
      :text="$t('bills.popup.share-bill-link.notifications.copied')"
    ></success-notification>
    <success-notification
      ref="billLinkCopiedNotification"
      :text="$t('bills.popup.share-bill-link.notifications.copied')"
    ></success-notification>
    <success-notification
      ref="billLinkSentNotification"
      :text="$t('bills.popup.share-bill-link.notifications.sent')"
    ></success-notification>
    <confirm-email-popup ref="confirmEmailPopup"></confirm-email-popup>
    <confirm-phone-popup ref="confirmPhonePopup"></confirm-phone-popup>
  </div>
</template>

<script>
import BasePopup from '@vue-app/components/base/popup/BasePopup.vue'
import PopupCloseButton from '@vue-app/components/base/button/PopupCloseButton.vue'
import SocialShareButtons from '@vue-app/components/base/share/SocialShareButtons.vue'
import LinkInput from '@vue-app/components/base/input/LinkInput.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import { Bill } from '@js-app/entities/Bill'
import TextAreaInput from '@vue-app/components/base/input/TextAreaInput.vue'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import { mapGetters, mapState } from 'vuex'
import ConfirmEmailPopup from '@vue-app/components/company/bills/popup/ConfirmEmailPopup.vue'
import ConfirmPhonePopup from '@vue-app/components/company/bills/popup/ConfirmPhonePopup.vue'
import { cloneDeep } from 'lodash'

export default {
  name: 'ShareBillLinkPopup',
  components: {
    ConfirmPhonePopup,
    ConfirmEmailPopup,
    TextAreaInput,
    SuccessNotification,
    LinkInput,
    SocialShareButtons,
    PopupCloseButton,
    BasePopup,
  },
  props: {
    bill: Bill,
    currency: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      notificationShowing: false,
      content: ``,
      _bill: null,
      enabledSocials: ['email', 'telegram', 'viber'],
    }
  },
  computed: {
    ...mapState(['selectedCompany']),
    ...mapGetters(['userData']),
  },
  created() {
    this._bill = cloneDeep(this.bill)
  },
  methods: {
    open(bill = null) {
      if (bill) {
        this._bill = bill
      }

      const payerName = this._bill.payerFullNamePlain.toString().trim()
      const forText = payerName
        ? this.$t('share-bill-link-popup.forText', { name: payerName })
        : ''

      this.content = this.$t('share-bill-link-popup.shareContent', {
        forText: forText,
        companyName: this._bill.companyName,
        invoiceNumber: this._bill.partnerOrderName,
        sum: this._bill.sum,
        link: this._bill.link,
      })

      this.$authorizer
        .can('read', 'bill_share_sms', this.userData.UserID.toString())
        .then((isCan) => {
          if (isCan) {
            if (!this.enabledSocials.includes('sms')) {
              this.enabledSocials.push('sms')
            }
          }
        })

      return this.$refs.popup.open()
    },
    async handleLinkCopied() {
      this.notificationShowing = true
      await this.$refs.billLinkCopiedNotification.show()

      this.notificationShowing = false
    },
    async handleLinkSent() {
      this.notificationShowing = true
      await this.$refs.billLinkSentNotification.show()

      this.notificationShowing = false
    },
    handleShareAction(action) {
      switch (action) {
        case 'email': {
          this.shareViaEmail()
          break
        }
        case 'sms': {
          this.shareViaSms()
          break
        }
      }
    },
    async shareViaEmail() {
      const email = await this.$refs.confirmEmailPopup.open(this._bill.email)

      if (!email || email.toString().trim().length === 0) {
        return
      }

      try {
        await CompaniesApi.shareBill(this.selectedCompany, {
          email: email,
          bill_id: this._bill.partnerOrderName || this._bill.id,
          payer_full_name: this._bill.payerFullNamePlain.toString(),
          company_name: this._bill.companyName,
          sum: this._bill.sum,
          bill_link: this._bill.link,
          purpose: this._bill.purpose,
        })
        this.handleLinkSent()
      } catch (e) {
        console.error(e)
      }
    },
    async shareViaSms() {
      const phone = await this.$refs.confirmPhonePopup.open(this._bill.phone)

      if (!phone || phone.toString().trim().length === 0) {
        return
      }
      try {
        await CompaniesApi.shareBillSms(this.selectedCompany, {
          phone: phone,
          message: this.content,
        })
        this.handleLinkSent()
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.share-bill-link {
  :deep(.popup) {
    width: 395px;
    padding: 30px 0 52px;

    .popup__actions {
      display: none;
    }
  }

  &__header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 30px;
    height: 30px;
    margin-bottom: 38px;
  }

  &__title {
    font-weight: 600;
    font-size: 16px;
    line-height: 18px;
    color: #585f6d;
  }

  &__close {
    position: absolute;
    right: 30px;
    top: 50%;
    transform: translateY(-50%);
  }

  &__buttons {
    margin-top: 25px;
  }

  &__content {
    padding: 0 52px;
  }

  &__text {
    :deep(.input__el) {
      padding-right: 50px;
      height: 210px;
    }
  }

  &__input {
    margin-top: 25px;
  }
}
</style>
